import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import { parseProject, type Project } from '../core/schema';
import { getAssetBlob, putAssetBlob, saveProject } from './persistence';
import { mediaRegistry } from './mediaRegistry';

const MIME = 'application/x-open-motion-project';

function extensionFor(name: string, mime: string): string {
  const fromName = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  if (fromName.length < 12) return fromName;
  if (mime === 'video/mp4') return '.mp4';
  if (mime === 'audio/mpeg') return '.mp3';
  if (mime === 'image/png') return '.png';
  return '.bin';
}

export async function createProjectPackage(project: Project, includeMedia = true): Promise<Blob> {
  const entries: Record<string, Uint8Array> = {
    'manifest.json': strToU8(JSON.stringify({ format: 'open-motion-project', formatVersion: 1, createdBy: 'Open Motion Studio', project }, null, 2))
  };
  if (includeMedia) {
    for (const asset of Object.values(project.assets)) {
      const blob = await getAssetBlob(project.id, asset.id);
      if (blob) entries[`assets/${asset.id}${extensionFor(asset.name, asset.mimeType)}`] = new Uint8Array(await blob.arrayBuffer());
    }
  }
  return new Blob([zipSync(entries, { level: 3 }) as unknown as BlobPart], { type: MIME });
}

export async function importProjectPackage(file: File): Promise<Project> {
  const entries = unzipSync(new Uint8Array(await file.arrayBuffer()));
  const manifestBytes = entries['manifest.json'];
  if (!manifestBytes) throw new Error('This package has no manifest.json file.');
  const manifest = JSON.parse(strFromU8(manifestBytes)) as { format?: string; project?: unknown };
  if (manifest.format !== 'open-motion-project') throw new Error('Unsupported project package.');
  const project = parseProject(manifest.project);
  for (const asset of Object.values(project.assets)) {
    const path = Object.keys(entries).find((entry) => entry.startsWith(`assets/${asset.id}.`));
    if (!path) { asset.missing = true; asset.cacheStatus = 'missing'; continue; }
    const blob = new Blob([entries[path] as unknown as BlobPart], { type: asset.mimeType });
    await putAssetBlob(project.id, asset.id, blob);
    mediaRegistry.register(project.id, asset.id, blob);
    asset.missing = false;
    asset.cacheStatus = 'local';
  }
  await saveProject(project, true);
  return project;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
